import React from "react";
import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar";
import { ResourceStrings } from "../../ResourceStrings";
import { NotificationInfo, NotificationType } from "../../store/appState";
import styled from "styled-components/macro";
import * as Actions from "../../store/actions";

/**
 * Predefined notification keys for contextual notifications.
 * This enum provides type safety and better discoverability of notification contexts.
 */
export enum NotificationKey {
  /** Notifications related to profile attachment operations (upload, delete, etc.) */
  ProfileAttachments = "profileAttachments",
}

export interface ContextualNotificationDisplayProps {
  notifications: NotificationInfo[] | null;
  notificationKey: NotificationKey;
  className?: string;
}

export default function ContextualNotificationDisplay(props: ContextualNotificationDisplayProps): JSX.Element {
  const { notifications, notificationKey, className } = props;

  if (!notifications || notifications.length === 0) {
    return null;
  }

  const getMessageBarType = (type: NotificationType): MessageBarType => {
    switch (type) {
      case "error":
        return MessageBarType.error;
      case "warning":
        return MessageBarType.warning;
      case "information":
        return MessageBarType.info;
      default:
        return MessageBarType.info;
    }
  };

  const handleDismiss = (notification: NotificationInfo) => {
    if (notification.canDismiss) {
      if (notification.onDismiss) {
        notification.onDismiss();
      } else {
        Actions.clearContextualNotification(notificationKey);
      }
    }
  };

  // Filter out null notifications before mapping
  const validNotifications = notifications.filter((notification): notification is NotificationInfo => 
    notification !== null && notification !== undefined
  );

  if (validNotifications.length === 0) {
    return null;
  }

  return (
    <Wrapper className={className}>
      {validNotifications.map((notification, index) => (
        <MessageBar
          key={index}
          messageBarType={getMessageBarType(notification.type)}
          onDismiss={notification.canDismiss ? () => handleDismiss(notification) : undefined}
          dismissButtonAriaLabel={ResourceStrings.close()}
        >
          <strong>{notification.title}</strong>
          {notification.message && (
            <>
              <br />
              {notification.message}
            </>
          )}
        </MessageBar>
      ))}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-bottom: 1rem;

  .ms-MessageBar {
    margin-bottom: 0.5rem;
  }

  .ms-MessageBar:last-child {
    margin-bottom: 0;
  }
`;
